import type { SelectOption } from '@movingimage-evp/mi-ui-component-library';
import type { Descendant } from 'slate';
import type {
  GetLsproDataProtectionQuery,
  StandardLanguage,
  UpdateLocalizedDataProtectionEntryInput,
} from 'src/generated/graphql-manager';

import {
  Heading,
  InputDescription,
  Paragraph,
  SaveButton,
  Select,
  Tab,
  Tabs,
} from '@movingimage-evp/mi-ui-component-library';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseContent } from 'src/components/rich-text-editor';
import { useGetLsproDataProtectionQuery, useUpdateLsproDataProtectionMutation } from 'src/generated/graphql-manager';
import { getLanguageKey } from 'src/utils';

import { LanguageEditor } from './language-editor';
import { useCurrentUser } from '../../../hooks/current-user';

import managerStyles from '../../../manager.module.css';

function extractLanguages(data: GetLsproDataProtectionQuery | undefined): StandardLanguage[] {
  return (
    data?.lsproSettings?.languages?.reduce((accumulator: StandardLanguage[], language: any) => {
      if (language.__typename === 'StandardLanguage') {
        accumulator.push(language);
      }
      return accumulator;
    }, []) || []
  );
}

export function DataProtectionPage() {
  const { lsproId } = useCurrentUser();
  const { t } = useTranslation();

  const [dataProtectionLanguages, setDataProtectionLanguages] = useState<StandardLanguage[]>([]);
  const [dataProtectionEntries, setDataProtectionEntries] = useState<UpdateLocalizedDataProtectionEntryInput[]>([]);
  const [editorRefreshKeys, setEditorRefreshKeys] = useState<number[]>([]);

  const { data } = useGetLsproDataProtectionQuery({
    variables: { lsproId },
    onCompleted: (data) => {
      const languages = extractLanguages(data);

      const entries = data?.lsproSettings?.dataProtectionForm?.entries || [];

      const filteredEntries = entries
        .filter((entry) => entry.language.__typename === 'StandardLanguage')
        .map((entry) => ({
          language: entry.language.__typename === 'StandardLanguage' ? entry.language.code : '',
          text: entry.text,
        }));

      setDataProtectionEntries(filteredEntries);

      // Filter languages to include only those with corresponding entries
      const filteredLanguages = languages.filter((language) =>
        entries.some((entry) => {
          if (entry.language.__typename === 'StandardLanguage') {
            return entry.language.code === language.code;
          }
          return false;
        })
      );

      setDataProtectionLanguages(filteredLanguages);
      setEditorRefreshKeys(languages.map(() => Math.random()));
    },
  });

  const languages = extractLanguages(data);

  const languagesOptions = languages.map((language) => ({
    key: language.code,
    label: t(`manager.webcastSetup.language.supportedLanguages.${getLanguageKey(language.code)}`),
  }));

  const languagesValue = languagesOptions.filter((option) =>
    dataProtectionLanguages.some((language) => language.code === option?.key)
  );

  const [updateLsproDataProtectionMutation, { loading: updateLsproDataProtectionLoading }] =
    useUpdateLsproDataProtectionMutation({
      variables: { input: { lsproId, entries: dataProtectionEntries } },
      update: (cache, { data }) => {
        if (data?.updateLiveStreamProDataProtectionForm.__typename !== 'UpdateDataProtectionFormSuccess') return;

        cache.modify({
          id: cache.identify({ __typename: 'LiveStreamProSettings', lsproId }),
          fields: {
            dataProtectionForm() {
              return {
                entries: dataProtectionEntries.map((entry) => ({
                  languages: { code: entry.language },
                  text: entry.text,
                })),
              };
            },
          },
        });
      },
    });

  const handleTabChange = useCallback(() => {
    setEditorRefreshKeys(languages.map(() => Math.random()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectedLanguageChange = (options: SelectOption | SelectOption[]) => {
    const selectedLanguages = languages.filter((language) =>
      options.some((option: SelectOption) => option.key === language.code)
    );
    setDataProtectionLanguages(selectedLanguages);

    const updatedEntries = options.map(
      (option: SelectOption) =>
        dataProtectionEntries.find((entry) => entry.language === option.key) || { language: option.key, text: '' }
    );
    setDataProtectionEntries(updatedEntries);
  };

  const handleOnDescriptionChange = (data: Descendant[], languageCode: StandardLanguage['code']) => {
    setDataProtectionEntries((prevEntries) => {
      const updatedEntries = [...prevEntries];
      const existingEntryIndex = prevEntries.findIndex((entry) => entry.language === languageCode);

      updatedEntries[existingEntryIndex] = {
        ...updatedEntries[existingEntryIndex],
        text: JSON.stringify(data),
      };

      return updatedEntries;
    });
  };

  return (
    <main className={managerStyles.main} data-testid="data-protection-page">
      <Heading className={managerStyles.grayText} data-testid="data-protection-heading">
        {t('manager.channelSettings.dataProtection.heading')}
      </Heading>

      <Paragraph data-testid="data-protection-description">
        {t('manager.channelSettings.dataProtection.paragraph')}
      </Paragraph>

      <InputDescription label={t('manager.channelSettings.dataProtection.languageLabel')}>
        <Select
          data-testid="data-protection-language-select"
          optionsIdProperty="key"
          value={languagesValue}
          options={languagesOptions}
          onChange={handleSelectedLanguageChange}
          placeholder={t('manager.channelSettings.dataProtection.languagePlaceholder')}
        />
      </InputDescription>

      {dataProtectionLanguages && dataProtectionLanguages.length === 1 && (
        <LanguageEditor
          data-testid={`data-protection-language-editor-${dataProtectionLanguages[0].code}`}
          key={editorRefreshKeys[3]}
          language={dataProtectionLanguages[0].code}
          description={parseContent(dataProtectionEntries[0]?.text)}
          onDescriptionChange={(data) => handleOnDescriptionChange(data, dataProtectionLanguages[0].code)}
        />
      )}

      {dataProtectionLanguages && dataProtectionLanguages.length > 1 && (
        <Tabs compact={languages.length <= 4} onSelectTab={handleTabChange} data-testid="data-protection-tabs">
          {dataProtectionLanguages.map((language, index) => {
            const translatedLanguage = t(
              `manager.webcastSetup.language.supportedLanguages.${getLanguageKey(language.code)}`
            );

            return (
              <Tab label={translatedLanguage} key={language.code} data-testid={`data-protection-tab-${language.code}`}>
                <LanguageEditor
                  data-testid={`data-protection-language-editor-${language.code}`}
                  key={editorRefreshKeys[index]}
                  language={language.code}
                  description={parseContent(
                    dataProtectionEntries.find((entry) => entry.language === language.code)?.text
                  )}
                  onDescriptionChange={(data) => handleOnDescriptionChange(data, language.code)}
                />
              </Tab>
            );
          })}
        </Tabs>
      )}

      <div className={managerStyles.buttons}>
        <SaveButton
          data-testid="data-protection-submit-button"
          onClick={updateLsproDataProtectionMutation}
          saving={updateLsproDataProtectionLoading}
          disabled={updateLsproDataProtectionLoading}
        />
      </div>
    </main>
  );
}
